import React, { useState, useEffect, useCallback } from 'react';
import MapComponent, { MapMarker } from './MapComponent';
import SearcherList, { Searcher } from './SearcherList';
import PlayerChat from './PlayerChat'; // Import PlayerChat
import SendMessageModal from './SendMessageModal'; // Import SendMessageModal
import { loQueSeaAFloat } from '../utils/TypeUtils';

interface PlayerDashboardProps {
  registeredPlayerId: string | null;
  registeredPlayerLocation: { latitude: number; longitude: number } | null;
  registeredPlayerName: string | null;
  openChatWindows: Array<{ id: string; name: string; gameAddress: string; gameDate: string; gameTime: string }>;
  handleCloseChat: (searcherId: string) => void;
  onNewMessage: (newSearcherId: string) => void;
  onOpenChatWithSearcher: (searcherId: string, searcherName: string, gameAddress: string, gameDate: string, gameTime: string) => void; // New callback
  onMapClickForRegistration?: (latlng: { lat: number; lng: number }) => void; // New prop for map clicks for registration
}

const PlayerDashboard: React.FC<PlayerDashboardProps> = ({
  registeredPlayerId,
  registeredPlayerLocation,
  registeredPlayerName,
  openChatWindows,
  handleCloseChat,
  onNewMessage,
  onOpenChatWithSearcher, // New prop
  onMapClickForRegistration, // New prop
}) => {
  const [mapCenter, setMapCenter] = useState<[number, number]>([-34.6037, -58.3816]); // Buenos Aires coordinates
  const [mapZoom, setMapZoom] = useState<number>(13);
  const [searchers, setSearchers] = useState<Searcher[]>([]);
  const [maximizedChatId, setMaximizedChatId] = useState<string | null>(null); // New state to track maximized chat
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<{ [searcherId: string]: number }>({}); // New state for unread messages
  const [showSendMessageModal, setShowSendMessageModal] = useState<boolean>(false);
  const [selectedSearcher, setSelectedSearcher] = useState<Searcher | null>(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

  useEffect(() => {
    if (registeredPlayerLocation) {
      setMapCenter([registeredPlayerLocation.latitude, registeredPlayerLocation.longitude]);
    } else if (searchers.length > 0) {
      // If player location is not available, center on the first searcher
      setMapCenter([searchers[0].latitude, searchers[0].longitude]);
    }
  }, [registeredPlayerLocation, searchers]);

  const fetchSearchers = useCallback(async () => {
    console.log('Attempting to fetch searchers from:', `${backendUrl}/api/searchers`);
    try {
      const response = await fetch(`${backendUrl}/api/searchers`);
      if (response.ok) {
        const data = await response.json();
        // Ensure latitude and longitude are numbers
        const typedSearchers: Searcher[] = data.searchers.map((s: any) => ({
          ...s,
          latitude: loQueSeaAFloat(s.latitude),
          longitude: loQueSeaAFloat(s.longitude),
        }));
        setSearchers(typedSearchers);
        console.log("Searchers Antes de setSearchers", searchers);
      } else {
        console.error('Error fetching searchers:', await response.json());
      }
    } catch (error) {
      console.error('Network error fetching searchers:', error);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchSearchers();

    const interval = setInterval(() => {
      fetchSearchers();
    }, 30000); // Refresh searchers every 30 seconds

    return () => clearInterval(interval);
  }, [fetchSearchers]);

  // Long polling for searcher online status
  useEffect(() => {
    let isMounted = true;

    const pollSearcherOnlineStatus = async () => {
      while (isMounted) {
        try {
          const response = await fetch(`${backendUrl}/api/searchers/online-status`);
          if (response.ok) {
            const data = await response.json();
            if (data.searchersOnlineStatus && data.searchersOnlineStatus.length > 0) {
              setSearchers(prevSearchers => {
                const updatedSearchers = [...prevSearchers];
                data.searchersOnlineStatus.forEach((updatedSearcher: Searcher) => {
                  const index = updatedSearchers.findIndex(s => s.id === updatedSearcher.id);
                  if (index !== -1) {
                    updatedSearchers[index] = {
                      ...updatedSearchers[index],
                      isOnline: updatedSearcher.isOnline,
                      latitude: loQueSeaAFloat(updatedSearcher.latitude),
                      longitude: loQueSeaAFloat(updatedSearcher.longitude),
                    };
                  } else if (updatedSearcher.isOnline) {
                    // If a new searcher comes online, add them to the list
                    updatedSearchers.push({
                      ...updatedSearcher,
                      latitude: loQueSeaAFloat(updatedSearcher.latitude),
                      longitude: loQueSeaAFloat(updatedSearcher.longitude),
                    });
                  }
                });
                return updatedSearchers;
              });
            }
          }
        } catch (error) {
          console.error('Long polling for searcher online status failed:', error);
        }
      }
    };

    pollSearcherOnlineStatus();

    return () => {
      isMounted = false;
    };
  }, [backendUrl]);

  const handleSendMessageClick = (searcher: Searcher) => {
    setSelectedSearcher(searcher);
    console.log("SelectedSearcher Antes de setSelectedSearcher", selectedSearcher);
    setShowSendMessageModal(true);
  };

  const mapMarkers: MapMarker[] = searchers.map(searcher => ({
    id: searcher.id,
    latitude: searcher.latitude,
    longitude: searcher.longitude,
    name: searcher.name,
    isOnline: searcher.isOnline,
    type: 'searcher',
    gameAddress: searcher.gameAddress,
    gameDate: searcher.gameDate,
    gameTime: searcher.gameTime,
    hasActiveChat: openChatWindows.some(chat => chat.id === searcher.id),
    onSendMessageClick: handleSendMessageClick, // Add this for searchers
    onViewChatClick: (searcherId: string) => {
      const searcher = searchers.find(s => s.id === searcherId);
      if (searcher) {
        onOpenChatWithSearcher(searcher.id, searcher.name, searcher.gameAddress, searcher.gameDate, searcher.gameTime);
      }
    },
  }));

  if (registeredPlayerLocation && registeredPlayerId) {
    mapMarkers.push({
      id: registeredPlayerId,
      latitude: registeredPlayerLocation.latitude,
      longitude: registeredPlayerLocation.longitude,
      name: registeredPlayerName || 'You',
      type: 'location',
    });
  }

  const handleMapClick = (latlng: { lat: number; lng: number }) => {
    console.log('Map clicked at:', latlng);
    if (onMapClickForRegistration) {
      onMapClickForRegistration(latlng);
    }
  };

  const handleMaximizeChat = (chatId: string | null) => {
    setMaximizedChatId(chatId);
    if (chatId) {
      setUnreadMessagesCount(prev => ({ ...prev, [chatId]: 0 })); // Reset unread count when chat is maximized
    }
  };

  const handleNewMessage = (searcherId: string) => {
    setUnreadMessagesCount(prev => ({
      ...prev,
      [searcherId]: (prev[searcherId] || 0) + 1,
    }));
    onNewMessage(searcherId); // Call original onNewMessage if it does other things
  };

  const handleCloseSendMessageModal = () => {
    setShowSendMessageModal(false);
    setSelectedSearcher(null);
  };

  const handleSendMessage = async (recipientId: string, content: string, senderLatitude: number, senderLongitude: number, searcherId: string | null, gameDate: string, gameTime: string) => {
    if (!registeredPlayerId || !registeredPlayerName || !content) return;

    try {
      console.log("SelectedSearcher antes de stringify", selectedSearcher);
      const response = await fetch(`${backendUrl}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          senderId: registeredPlayerId,
          recipientId: searcherId, // The searcher is the recipient
          content,
          gameAddress: selectedSearcher?.gameAddress || '',
          gameDate: selectedSearcher?.gameDate || 'N/A', // Provide a default value if empty
          gameTime: selectedSearcher?.gameTime || 'N/A', // Provide a default value if empty
          senderLatitude,
          senderLongitude,
          searcherId, // The searcher's ID
          senderName: registeredPlayerName,
        }),
      });

      if (response.ok) {
        console.log('Message sent successfully to searcher.');
        if (selectedSearcher) {
          onOpenChatWithSearcher(selectedSearcher.id, selectedSearcher.name, selectedSearcher.gameAddress, selectedSearcher.gameDate, selectedSearcher.gameTime);
        }
      } else {
        console.error('Error sending message to searcher:', await response.json());
      }
    } catch (error) {
      console.error('Network error sending message to searcher:', error);
    }
  };

  return (
    <div className="player-dashboard-container">
      <div className="searcher-list-panel">
        <SearcherList searchers={searchers} />
      </div>
      <div className="map-panel">
        <MapComponent center={mapCenter} zoom={mapZoom} onMapClick={handleMapClick} markers={mapMarkers} />
      </div>

      {registeredPlayerId && registeredPlayerLocation && registeredPlayerName && selectedSearcher && (
        <SendMessageModal
          show={showSendMessageModal}
          onHide={handleCloseSendMessageModal}
          player={{ id: registeredPlayerId, name: registeredPlayerName, contact: '', isOnline: true }} // Player is the sender
          onSend={handleSendMessage}
          senderLatitude={registeredPlayerLocation.latitude}
          senderLongitude={registeredPlayerLocation.longitude}
          searcherUniqueId={selectedSearcher.id} // Searcher is the recipient
          gameAddress={selectedSearcher.gameAddress}
          gameDate={selectedSearcher.gameDate}
          gameTime={selectedSearcher.gameTime}
          recipientName={selectedSearcher.name} // Pass the searcher's name as recipientName
          recipientIsSearcher={true}
        />
      )}
      {registeredPlayerId && (
        <div className="player-chat-windows-container">
          {openChatWindows.map(chat => {
            console.log('PlayerDashboard: Rendering PlayerChat with chat object:', chat);
            return (
              <PlayerChat
                key={chat.id}
                registeredPlayerId={registeredPlayerId}
                registeredPlayerLocation={registeredPlayerLocation}
                registeredPlayerName={registeredPlayerName}
                searcherId={chat.id}
                searcherName={chat.name}
                searcherGameAddress={chat.gameAddress}
                searcherGameDate={chat.gameDate} // Pass gameDate
                searcherGameTime={chat.gameTime} // Pass gameTime
                onCloseChat={() => handleCloseChat(chat.id)}
                onNewMessage={handleNewMessage} // Use the new handler
                isMaximized={maximizedChatId === chat.id} // Pass maximization state
                onMaximizeToggle={handleMaximizeChat} // Pass toggle function
                unreadCount={unreadMessagesCount[chat.id] || 0} // Pass unread count
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerDashboard;